import { ButtonLink } from '@/components/ui/button';
import { BadgeCheck, Building2, DatabaseZap, FileSearch, Globe2, ShieldCheck, Sparkles, Zap } from 'lucide-react';
import { CompanySearch } from '@/components/company/company-search';
import { Page } from '@/components/layout/page';
import { OfferCard } from '@/components/payment/offer-card';
import { DevNote } from '@/components/ui/dev-note';
import { Badge, Card, CardBody, Container, Section, SectionHeading } from '@/components/ui/primitives';
import { useSite } from '@/hooks/use-site';
import { formatCountry } from '@/lib/format';
import { paths } from '@/lib/paths';

/**
 * Page d'accueil.
 *
 * Elle remplit trois rôles à la fois :
 *
 * 1. l'entrée du parcours visiteur (recherche puis achat) ;
 * 2. le point de départ du robot de vérification de mise en production, qui
 *    y cherche le bouton `data-dashlite="offer-checkout"` — porté par la
 *    carte d'offre plus bas ;
 * 3. une vitrine de ce que l'API sait faire, pour les développeurs qui
 *    découvrent la plateforme.
 *
 * Aucun appel d'API ne lui est propre : tout ce qu'elle affiche vient de
 * `GET /site`, déjà chargé au démarrage. La recherche, elle, n'appelle qu'à
 * partir de trois caractères saisis.
 */
export default function HomePage() {
    const site = useSite();

    return (
        <Page title="Les informations officielles des entreprises">
            <Hero />
            <TrustStrip />
            <HowItWorks />
            <Coverage />
            <Pricing />
            {site.environment === 'test' && <TestDataHint />}
        </Page>
    );
}

function Hero() {
    const site = useSite();

    return (
        <div className="from-brand-50 via-surface to-surface border-line border-b bg-gradient-to-b">
            <Container className="py-20 sm:py-28">
                <div className="max-w-3xl">
                    <Badge tone="brand" className="mb-6">
                        <Sparkles className="size-3.5" aria-hidden />
                        Registres officiels — {formatCountry(site.country)}
                    </Badge>

                    <h1 className="font-display text-5xl leading-[1.05] tracking-tight sm:text-6xl">
                        Toutes les informations
                        <br />
                        d'une entreprise, en une fiche.
                    </h1>

                    <p className="text-ink-soft mt-6 max-w-2xl text-lg leading-relaxed">
                        Identité, immatriculation, capital, activité, dirigeants, effectifs et juridiction compétente.
                        DocInfoPro interroge les registres officiels et vous restitue une fiche claire, lisible et
                        datée.
                    </p>
                </div>

                <div className="mt-10">
                    <CompanySearch variant="hero" autoFocus />
                </div>
            </Container>
        </div>
    );
}

const TRUST_POINTS = [
    { icon: DatabaseZap, label: 'Sources officielles', detail: 'Registres nationaux, sans intermédiaire' },
    { icon: Zap, label: 'Réponse immédiate', detail: 'Fiches en cache, renouvelées en arrière-plan' },
    { icon: ShieldCheck, label: 'Paiement sécurisé', detail: 'Traité par Stripe, jamais sur nos serveurs' },
    { icon: BadgeCheck, label: 'Résiliation en ligne', detail: 'À tout moment, sans justification' },
];

function TrustStrip() {
    return (
        <div className="border-line bg-surface-sunken border-b">
            <Container className="grid gap-6 py-8 sm:grid-cols-2 lg:grid-cols-4">
                {TRUST_POINTS.map((point) => (
                    <div key={point.label} className="flex items-start gap-3">
                        <point.icon className="text-brand-600 mt-0.5 size-5 shrink-0" aria-hidden />
                        <div>
                            <p className="text-ink text-sm font-semibold">{point.label}</p>
                            <p className="text-ink-muted text-xs">{point.detail}</p>
                        </div>
                    </div>
                ))}
            </Container>
        </div>
    );
}

const STEPS = [
    {
        icon: FileSearch,
        title: 'Recherchez',
        body: "Par nom ou par identifiant national. La recherche est publique et le pays est celui du site : aucun code pays à transmettre.",
        endpoint: 'GET /companies/search',
    },
    {
        icon: Building2,
        title: 'Payez sur place',
        body: "Le résultat choisi ouvre directement le paiement : l'aperçu public de l'entreprise d'un côté, la carte bancaire de l'autre. Aucun compte à créer au préalable — il est ouvert une fois le paiement abouti.",
        endpoint: 'POST /payment',
    },
    {
        icon: BadgeCheck,
        title: 'Consultez la fiche',
        body: "La fiche complète s'ouvre dès que votre accès est actif, et le reste pour toute la durée de votre offre.",
        endpoint: 'GET /companies/{id}',
    },
];

function HowItWorks() {
    return (
        <Section>
            <Container>
                <SectionHeading
                    eyebrow="Le parcours"
                    title="Trois étapes, et rien à installer"
                    description="Le même enchaînement pour toutes les entreprises, quel que soit le pays du registre interrogé."
                />

                <div className="mt-10 grid gap-6 md:grid-cols-3">
                    {STEPS.map((step, index) => (
                        <Card key={step.title}>
                            <CardBody className="space-y-3">
                                <div className="flex items-center gap-3">
                                    <span className="bg-brand-50 text-brand-600 flex size-10 items-center justify-center rounded-lg">
                                        <step.icon className="size-5" aria-hidden />
                                    </span>
                                    <span className="text-ink-muted font-mono text-xs">0{index + 1}</span>
                                </div>

                                <h3 className="text-ink font-sans text-base font-semibold">{step.title}</h3>
                                <p className="text-ink-soft text-sm leading-relaxed">{step.body}</p>

                                <p className="text-ink-muted border-line border-t pt-3 font-mono text-xs">
                                    {step.endpoint}
                                </p>
                            </CardBody>
                        </Card>
                    ))}
                </div>
            </Container>
        </Section>
    );
}

function Coverage() {
    const site = useSite();

    return (
        <Section className="bg-surface-sunken border-line border-y">
            <Container className="grid items-center gap-12 lg:grid-cols-2">
                <div>
                    <SectionHeading
                        eyebrow="Multi-pays"
                        title="Un seul format de fiche, quatre registres"
                        description="France, Belgique, Portugal, Suisse : la structure de la réponse est identique. Seuls le format de l'identifiant national et la densité des informations publiées changent d'un registre à l'autre."
                    />

                    <p className="text-ink-soft mt-6 text-sm leading-relaxed">
                        Ce site interroge le registre de{' '}
                        <strong className="text-ink">{formatCountry(site.country)}</strong>. Un site identique servant
                        un autre pays afficherait les mêmes écrans, sans une ligne de code différente : les clés
                        absentes valent <code className="font-mono text-xs">null</code>, elles ne disparaissent jamais.
                    </p>

                    <ButtonLink to={paths.integration} variant="secondary" size="sm" className="mt-6">
                        <Globe2 className="size-4" aria-hidden />
                        Comprendre le schéma normalisé
                    </ButtonLink>
                </div>

                <Card>
                    <CardBody className="space-y-4">
                        {[
                            { country: 'France', id: 'SIREN', format: '9 chiffres', example: '111222333' },
                            { country: 'Belgique', id: 'BCE / KBO', format: '10 chiffres', example: '0111222333' },
                            { country: 'Portugal', id: 'NIF / NIPC', format: '9 chiffres', example: '511222333' },
                            { country: 'Suisse', id: 'IDE / UID', format: 'CHE + 9 chiffres', example: 'CHE111222333' },
                        ].map((row) => (
                            <div key={row.country} className="border-line flex items-baseline justify-between gap-4 border-b pb-3 last:border-b-0 last:pb-0">
                                <div>
                                    <p className="text-ink text-sm font-medium">{row.country}</p>
                                    <p className="text-ink-muted text-xs">
                                        {row.id} — {row.format}
                                    </p>
                                </div>
                                <code className="text-ink-soft font-mono text-xs">{row.example}</code>
                            </div>
                        ))}
                    </CardBody>
                </Card>
            </Container>
        </Section>
    );
}

function Pricing() {
    return (
        <Section id="offre">
            <Container className="grid items-start gap-12 lg:grid-cols-2">
                <div>
                    <SectionHeading
                        eyebrow="Accès"
                        title="Une offre, sans surprise"
                        description="Le site n'a qu'une seule offre, active en permanence : pas de catalogue à parcourir, pas d'option cachée."
                    />

                    <p className="text-ink-soft mt-6 text-sm leading-relaxed">
                        Le tarif affiché à droite vient directement de l'API : il est lu dans la réponse de{' '}
                        <code className="font-mono text-xs">GET /site</code> et n'est jamais recopié dans le code du
                        front. Modifier le prix dans la console suffit à le voir changer ici.
                    </p>

                    <ButtonLink to={paths.pricing} variant="secondary" size="sm" className="mt-6">
                        Voir le détail de l'offre
                    </ButtonLink>
                </div>

                <OfferCard />
            </Container>
        </Section>
    );
}

/**
 * Aide affichée uniquement en environnement de test, où les données servies
 * proviennent d'un jeu d'entreprises fictives. En production, cette section
 * disparaît d'elle-même : la condition porte sur `site.environment`, valeur
 * renvoyée par l'API.
 */
function TestDataHint() {
    return (
        <Section className="border-line border-t">
            <Container>
                <DevNote title="Vous êtes en environnement de test" endpoints={['GET /companies/search']}>
                    <p>
                        Aucun appel n'est fait aux fournisseurs réels : la recherche et les fiches sont servies depuis
                        un jeu d'entreprises fictives. Quelques identifiants à essayer pour la France :
                    </p>
                    <ul className="list-inside list-disc space-y-0.5 font-mono text-xs">
                        <li>111222333 — BOULANGERIE DUPONT (active)</li>
                        <li>555666777 — CAFE DE LA GARE (cessée)</li>
                        <li>000000000 — déclenche un 404 company_not_found</li>
                        <li>999999999 — déclenche un 503 upstream_unavailable</li>
                    </ul>
                    <p>
                        La recherche <code className="font-mono">__unavailable__</code> force elle aussi une panne de
                        fournisseur : de quoi vérifier l'affichage de vos écrans d'erreur sans rien casser.
                    </p>
                </DevNote>
            </Container>
        </Section>
    );
}
